import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import {
  ArrowLeft, Radio, Timer, Trophy, MapPin, User, Car,
  Activity, Flag, Loader2, AlertTriangle, Maximize2, Minimize2, Zap, Pause,
} from 'lucide-react';
import {
  LapDeltaBadge, TrackPositionBar, FlagCautionIndicator, IncidentPill,
  isShiftLight, isCaution, deriveRigState, RIG_STATE_BADGE,
} from '@/components/telemetry/TelemetryVisuals';
import ShiftLights from '@/components/telemetry/ShiftLights';
import PedalBar from '@/components/telemetry/PedalBar';
import FpsBadge from '@/components/telemetry/FpsBadge';
import { useSimLogo } from '@/lib/BrandAssetsContext';
import { prettifyAssettoName, prettifyDriverName, isAcFamily } from '@/lib/telemetryPrettify';

const SIM_BADGE = {
  iracing: { label: 'iRACING', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', logoPath: '/assets/sims/iracing.svg', monogram: 'iR' },
  assetto_corsa: { label: 'ASSETTO CORSA', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', logoPath: '/assets/sims/ac.svg', monogram: 'AC' },
  assetto_corsa_competizione: { label: 'ACC', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30', logoPath: '/assets/sims/acc.svg', monogram: 'ACC' },
  none: { label: 'IDLE', cls: 'bg-white/5 text-white/40 border-white/10' },
};

// Inline sim logo — checks BrandAssets context first, falls back to the
// static /assets/sims/ path, then to the monogram if both fail.
function SimLogo({ logoPath, monogram, simKey }) {
  const [failed, setFailed] = useState(false);
  const contextLogo = useSimLogo(simKey);
  const src = contextLogo || logoPath;
  if (!src || failed) {
    return (
      <span className="inline-flex items-center justify-center h-4 px-1 font-mono font-bold text-[9px] tracking-tight">
        {monogram}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      className="h-4 w-auto object-contain"
    />
  );
}

function fmtGear(g) {
  if (g === null || g === undefined) return '–';
  if (g < 0) return 'R';
  if (g === 0) return 'N';
  return String(g);
}

export default function RigDetail() {
  const { rigKey } = useParams();
  const [rig, setRig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [speedUnit, setSpeedUnit] = useState(() => {
    try { return localStorage.getItem('mr_speed_unit') || 'mph'; } catch { return 'mph'; }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleSpeedUnit = () => {
    const next = speedUnit === 'mph' ? 'kmh' : 'mph';
    setSpeedUnit(next);
    try { localStorage.setItem('mr_speed_unit', next); } catch {}
  };

  useEffect(() => {
    let alive = true;
    const load = () =>
      base44.entities.RigLiveFeed.filter({ rig_key: rigKey })
        .then((d) => { if (alive) { setRig(d[0] || null); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    load();
    const unsub = base44.entities.RigLiveFeed.subscribe((ev) => {
      if (ev.type === 'delete') { setRig((p) => (p && p.id === ev.id ? null : p)); return; }
      setRig((p) => {
        // Match by record ID once the rig is loaded (the reliable path —
        // partial update events may omit rig_key). Before the initial load
        // resolves, fall back to matching by rig_key from the event data.
        if (p && p.id === ev.id) return { ...p, ...ev.data, id: ev.id };
        if (!p && ev.data?.rig_key === rigKey) return { ...ev.data, id: ev.id };
        return p;
      });
    });
    return () => { alive = false; unsub(); };
  }, [rigKey]);

  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 2000); return () => clearInterval(t); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!rig) {
    return (
      <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
        <div className="text-center">
          <Radio className="w-10 h-10 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm">No rig found for <span className="text-white font-mono">{rigKey}</span>.</p>
          <Link to="/live" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">BACK TO LIVE FEEDS →</Link>
        </div>
      </div>
    );
  }

  const last = rig.last_update ? new Date(rig.last_update).getTime() : 0;
  const live = last && (Date.now() - last < 6000);
  const standby = !last || (Date.now() - last >= 60000);
  const simBadge = SIM_BADGE[rig.sim] || SIM_BADGE.none;
  const rpmPct = rig.max_rpm && rig.rpm ? Math.min(100, (rig.rpm / rig.max_rpm) * 100) : null;
  const accent = rig.theme === 'amg' ? '#6ee7b7' : rig.theme === 'porsche' ? '#e23b2e' : '#dd1f26';
  const accentText = rig.theme === 'amg' ? 'text-emerald-300' : rig.theme === 'porsche' ? 'text-[#e23b2e]' : 'text-primary';
  const shiftFlash = isShiftLight(rig);
  const showCaution = isCaution(rig.session_flags);
  const showPos = live && rig.position != null && rig.position > 0;
  const dimmed = !live;

  // Contextual rig state — replaces the old binary LIVE/STANDBY pill.
  const rigState = deriveRigState(rig);
  const isPaused = rigState === 'paused';
  const isInPits = rigState === 'in_pits';
  const stateBadge = RIG_STATE_BADGE[rigState];
  const stateDotColor = rigState === 'live' ? accent : (rigState === 'in_pits' || rigState === 'stale') ? '#f59e0b' : rigState === 'paused' ? '#94a3b8' : '#ffffff44';
  const stateTextClass = rigState === 'live' ? accentText : stateBadge.textClass;

  // During pause, zero out pedals and RPM to prevent stuck high-RPM traces.
  // During IN PITS, live inputs stay active so spectators see throttle revs.
  const displayThrottle = isPaused ? 0 : rig.throttle;
  const displayBrake = isPaused ? 0 : rig.brake;
  const displayRpm = isPaused ? null : rig.rpm;
  const displayRpmPct = isPaused ? null : rpmPct;

  // Display-side prettifier safety net for raw Kunos IDs / placeholder drivers.
  const displayDriver = prettifyDriverName(rig.driver_name, rig.rig_key) || 'Guest Driver';
  const displayCar = isAcFamily(rig.sim) ? prettifyAssettoName(rig.car) : rig.car;
  const displayTrack = isAcFamily(rig.sim) ? prettifyAssettoName(rig.track) : rig.track;

  return (
    <div className={`min-h-screen bg-background ${isFullscreen ? '' : 'pt-16'}`}>
      {/* sticky sub-header with back link */}
      <div className={`border-b border-white/5 bg-[#0a0b0e]/80 backdrop-blur-sm sticky ${isFullscreen ? 'top-0' : 'top-[var(--header-offset)]'} z-20`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/live" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-xs font-bold tracking-widest transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> LIVE FEEDS
          </Link>
          {showCaution && <FlagCautionIndicator sessionFlags={rig.session_flags} size="lg" />}
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-6 lg:px-8 py-6 space-y-4 ${dimmed ? 'opacity-80' : ''}`}>
        {/* HERO BANNER — full width */}
        <div className="relative bg-[#121214] border border-white/10 overflow-hidden">
          {/* accent top border */}
          <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: accent }} />
          {live && !shiftFlash && <div className="absolute top-0 left-0 right-0 h-0.5 animate-pulse" style={{ background: accent }} />}

          <div className="p-6 lg:p-8">
            {/* top row: rig key + status */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/30 text-[10px] font-mono font-bold tracking-widest uppercase">{rig.rig_key}</span>
              <div className="flex items-center gap-2.5">
                <span className={`inline-flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase px-2 py-1 border ${simBadge.cls}`}>
                  <SimLogo logoPath={simBadge.logoPath} monogram={simBadge.monogram} simKey={rig.sim} />
                  {simBadge.label}
                </span>
                <button
                  onClick={toggleFullscreen}
                  className="text-white/40 hover:text-white transition-colors"
                  title={isFullscreen ? 'Exit fullscreen' : 'Spectator fullscreen'}
                >
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase ${stateTextClass}`}>
                  {stateBadge.icon === 'pause' ? (
                    <Pause className="w-3 h-3" />
                  ) : (
                    <span className="relative flex w-2 h-2">
                      {stateBadge.ping && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: stateDotColor }} />}
                      <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: stateDotColor }} />
                    </span>
                  )}
                  {stateBadge.label}
                </span>
              </div>
            </div>

            {/* main heading */}
            <h1 className="text-white font-black text-3xl md:text-5xl uppercase tracking-tight leading-none mb-5">
              {rig.rig_name || rig.rig_key}
            </h1>

            {/* motorsport identity badges */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge icon={<User className="w-3 h-3" />} label={displayDriver || '—'} />
              <Badge icon={<Car className="w-3 h-3" />} label={displayCar || '—'} />
              <Badge icon={<MapPin className="w-3 h-3" />} label={displayTrack || '—'} />
              {rig.session_type && (
                <Badge icon={<Flag className="w-3 h-3" />} label={rig.session_type} />
              )}
            </div>
          </div>
        </div>

        {/* caution banner */}
        {showCaution && (
          <div className="flex items-center gap-3 border border-amber-400/50 bg-amber-400/10 px-5 py-3 animate-caution-pulse">
            <Flag className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-bold tracking-widest uppercase text-sm">Caution — yellow flag is active</span>
          </div>
        )}

        {/* PAUSED banner — dimmed cockpit + central pause indicator */}
        {isPaused && (
          <div className="flex items-center justify-center gap-2 border border-slate-400/30 bg-slate-400/5 px-5 py-3">
            <Pause className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 font-bold tracking-widest uppercase text-sm">Session Paused</span>
          </div>
        )}

        {/* STANDBY OVERLAY — shown when last_update is null or > 60s old */}
        {standby && (
          <div className="relative bg-[#121214] border border-white/10 overflow-hidden">
            <div className="px-6 py-16 flex flex-col items-center justify-center gap-3">
              <Radio className="w-8 h-8 text-white/15" />
              <p className="text-white/30 text-sm font-bold tracking-widest uppercase">Standby — Awaiting Telemetry</p>
              <p className="text-white/20 text-xs">The rig will appear here as soon as the pusher connects.</p>
              <Link to="/live" className="inline-block mt-4 text-primary text-xs font-bold tracking-widest hover:underline">BACK TO LIVE FEEDS →</Link>
            </div>
          </div>
        )}

        {/* 2-COLUMN GRID: cockpit cluster (left 60%) + timing ribbon (right 40%) */}
        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-4 ${standby ? 'hidden' : ''}`}>
          {/* LEFT — COCKPIT INSTRUMENT CLUSTER */}
          <div className={`lg:col-span-3 bg-[#121214] border ${shiftFlash ? 'animate-shift-flash border-white/10' : 'border-white/10'} overflow-hidden ${isPaused ? 'opacity-60' : ''}`}>
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Radio className={`w-3.5 h-3.5 ${live ? accentText : 'text-white/30'}`} />
                <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Cockpit Instrument Cluster</span>
              </div>
              <FpsBadge fps={rig.fps} />
              <button
                onClick={toggleSpeedUnit}
                className="flex items-center border border-white/10 bg-black/30 text-[9px] font-bold tracking-widest uppercase"
              >
                <span className={`px-2 py-1 ${speedUnit === 'mph' ? 'bg-primary text-white' : 'text-white/40'}`}>MPH</span>
                <span className={`px-2 py-1 ${speedUnit === 'kmh' ? 'bg-primary text-white' : 'text-white/40'}`}>KM/H</span>
              </button>
            </div>

            <div className="p-6 lg:p-8">
              {/* gear + speed + pedals row — DDU housings */}
              <div className="flex items-stretch justify-between gap-4 mb-6">
                {/* vertical LED pedal bars — Brake left, Throttle right */}
                <div className="flex items-stretch justify-center gap-4 w-full max-w-[200px] bg-[#0a0a0c] rounded-2xl border border-white/10 shadow-[inset_0_2px_12px_rgba(0,0,0,0.8)] p-4">
                  <PedalBar label="Brake" value={displayBrake} color="#dd1f26" glowRgba="rgba(221,31,38,0.5)" />
                  <PedalBar label="Throttle" value={displayThrottle} color="#10b981" glowRgba="rgba(16,185,129,0.4)" />
                </div>

                {/* central gear + speed — MoTeC DDU housing */}
                <div className="relative flex-1 flex flex-col items-center justify-center bg-[#0a0a0c] rounded-2xl border border-white/10 shadow-[inset_0_2px_12px_rgba(0,0,0,0.8)] p-6 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#dd1f26]" />
                  {/* gear */}
                  <div className="text-center mb-5">
                    <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1">Gear</p>
                    <span className={`font-black text-7xl font-mono leading-none ${rig.gear && rig.gear > 0 ? 'text-[#dd1f26]' : 'text-white/50'}`}>
                      {fmtGear(rig.gear)}
                    </span>
                  </div>
                  {/* speed */}
                  <div className="text-center">
                    <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1">Speed</p>
                    <div className="flex items-baseline gap-2 justify-center">
                      <span className="text-white font-black text-5xl tabular-nums leading-none font-mono">
                        {rig.speed_kph != null
                          ? (speedUnit === 'mph' ? Math.round(rig.speed_kph * 0.621371) : Math.round(rig.speed_kph))
                          : '–'}
                      </span>
                      <span className="text-white/40 text-sm font-bold">{speedUnit === 'mph' ? 'mph' : 'km/h'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 16-LED progressive RPM shift lights */}
              <div className={`bg-[#0a0a0c] rounded-2xl border border-white/10 shadow-[inset_0_2px_12px_rgba(0,0,0,0.8)] p-4 ${shiftFlash ? 'animate-shift-flash' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/30 text-[10px] font-bold tracking-widest uppercase">RPM Shift Lights</span>
                  <span className="text-white/50 text-xs font-mono tabular-nums">
                    {displayRpm != null ? Math.round(displayRpm).toLocaleString() : '–'}
                    <span className="text-white/30"> / {rig.max_rpm ? Math.round(rig.max_rpm).toLocaleString() : '—'} RPM</span>
                  </span>
                </div>
                <ShiftLights rpmPct={displayRpmPct} shiftFlash={shiftFlash} />
                <div className="flex justify-between mt-1.5">
                  <span className="text-white/20 text-[9px] font-mono">0</span>
                  <span className="text-white/20 text-[9px] font-mono">{rig.max_rpm ? Math.round(rig.max_rpm).toLocaleString() : ''}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — TIMING & TRACK RIBBON */}
          <div className="lg:col-span-2 bg-[#121214] border border-white/10 overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
              <Timer className="w-3.5 h-3.5 text-white/30" />
              <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Timing & Track Ribbon</span>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              {/* timing stats grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {/* last lap */}
                <div className="bg-[#141416] rounded-xl border border-[#26262a] px-3 py-3">
                  <div className="flex items-center justify-between gap-1 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Timer className="w-3 h-3 text-white/40" />
                      <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">Last Lap</span>
                    </div>
                    <LapDeltaBadge delta={rig.lap_delta} variant="inline" />
                  </div>
                  {rig.lap_time ? (
                    <span className="text-white font-mono font-bold text-lg tabular-nums">{rig.lap_time}</span>
                  ) : (
                    <div>
                      <span className="text-white/25 font-mono font-bold text-lg tabular-nums">--:--.---</span>
                      {rig.lap === 1 && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#dd1f26]/15 border border-[#dd1f26]/40 text-[#dd1f26] text-[8px] font-bold font-mono tracking-widest">[OUT LAP]</span>
                      )}
                    </div>
                  )}
                </div>

                {/* best lap */}
                <div className="bg-[#141416] rounded-xl border border-[#26262a] px-3 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Trophy className="w-3 h-3" style={{ color: accent }} />
                    <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">Best Lap</span>
                  </div>
                  <span className={`font-mono font-bold text-lg tabular-nums ${rig.best_lap_time ? accentText : 'text-white/25'}`}>
                    {rig.best_lap_time || '--:--.---'}
                  </span>
                </div>

                {/* lap number */}
                <div className="bg-[#141416] rounded-xl border border-[#26262a] px-3 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Flag className="w-3 h-3 text-white/40" />
                    <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">Lap No.</span>
                  </div>
                  <span className="text-white font-mono font-bold text-lg tabular-nums">{rig.lap != null ? rig.lap : '–'}</span>
                </div>

                {/* position + incidents */}
                <div className="bg-[#141416] rounded-xl border border-[#26262a] px-3 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Zap className="w-3 h-3 text-white/40" />
                    <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">Position</span>
                  </div>
                  {showPos ? (
                    <span className="inline-flex items-center font-mono font-black text-lg px-2 border border-white/15 text-white">P{rig.position}</span>
                  ) : (
                    <span className="text-white/40 font-mono font-bold text-lg">–</span>
                  )}
                </div>
              </div>

              {/* IN PITS / PIT BOX sub-badge under timing tiles */}
              {isInPits && (
                <div className="mb-5">
                  <span className="inline-flex items-center gap-1.5 border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-amber-400 text-[10px] font-bold tracking-widest uppercase">
                    ● IN PITS
                  </span>
                </div>
              )}

              {/* incidents pill */}
              {rig.incidents != null && (
                <div className="mb-5">
                  <IncidentPill incidents={rig.incidents} size="lg" />
                </div>
              )}

              {/* track position ribbon with sector markers */}
              <div className="mt-auto pt-4 border-t border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Track Position
                  </span>
                  {rig.lap_dist_pct != null ? (
                    <span className="text-white/50 text-xs font-mono tabular-nums">
                      {`${Math.round(Number(rig.lap_dist_pct) * 100)}%`}
                    </span>
                  ) : (
                    <span className="text-white/25 text-[9px] font-bold tracking-widest uppercase">Sector timing active</span>
                  )}
                </div>
                {rig.lap_dist_pct != null ? (
                  <>
                    <TrackPositionBar pct={rig.lap_dist_pct} accent={accent} size="lg" />
                    {/* sector markers */}
                    <div className="relative flex justify-between mt-1">
                      {[0, 25, 50, 75, 100].map((s) => (
                        <div key={s} className="flex flex-col items-center">
                          <div className="w-px h-1.5 bg-white/10" />
                          <span className="text-white/20 text-[8px] font-mono mt-0.5">S{s === 0 ? '1' : s === 25 ? '2' : s === 50 ? '3' : s === 75 ? '4' : ''}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-2.5 bg-white/5 flex items-center justify-center">
                    <span className="text-white/15 text-[8px] font-mono tracking-widest uppercase">Awaiting track data</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Badge({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-3 py-1.5 text-white/70 text-xs font-medium">
      {icon}
      <span className="truncate max-w-[200px]">{label}</span>
    </span>
  );
}